"use client"

import {
  ListCard,
  ListCardTrailingActionsFooter,
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  MatchQualityBadge,
  EvaluationAnalysisInline,
} from '@yeshuman/sdk'
import type { OpportunityMatch, OpenOpportunityMatchHandler } from '@yeshuman/sdk'
import { type ReactNode } from 'react'
import { JobOpeningIdentityHeader } from '../job-opening-identity'

interface UnifiedOpportunityCardProps {
  opportunity: OpportunityMatch
  renderAction?: (opportunity: OpportunityMatch) => ReactNode
  onOpenMatch?: OpenOpportunityMatchHandler
}

export function UnifiedOpportunityCard({
  opportunity,
  renderAction,
  onOpenMatch,
}: UnifiedOpportunityCardProps) {
  const strategies = opportunity.found_by ?? []

  return (
    <ListCard
      className="flex min-h-0 min-w-0 w-full flex-1 flex-col transition-shadow"
      onOpen={
        onOpenMatch && opportunity.evaluation_id
          ? () => {
              const evaluationId = opportunity.evaluation_id
              if (!evaluationId) return
              onOpenMatch(
                evaluationId,
                `${opportunity.role_title} · ${opportunity.company_name}`,
                opportunity.opportunity_id,
              )
            }
          : undefined
      }
      openAriaLabel={
        onOpenMatch && opportunity.evaluation_id
          ? `Open match analysis for ${opportunity.role_title}`
          : undefined
      }
      header={
        <JobOpeningIdentityHeader
          title={opportunity.role_title}
          showPublicationBadge={false}
          organisationLabel={opportunity.company_name}
          trailing={
            opportunity.rank != null ? (
              <Badge variant="default">#{opportunity.rank}</Badge>
            ) : null
          }
        />
      }
      body={
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {opportunity.final_score != null ? (
              <MatchQualityBadge score={opportunity.final_score} />
            ) : null}
            {strategies.length > 0 &&
              strategies.map((s) => (
                <Badge key={s} variant="secondarySoft" className="text-xs font-normal">
                  {s}
                </Badge>
              ))}
          </div>
          {opportunity.evaluation_id ? (
            <EvaluationAnalysisInline
              evaluationId={opportunity.evaluation_id}
              analysisHeadline={opportunity.analysis_headline}
            />
          ) : opportunity.recommendation ? (
            <Collapsible className="rounded-md border border-border/60 bg-muted/30">
              <CollapsibleTrigger className="group w-full cursor-pointer px-3 py-2 text-left text-sm font-medium">
                <span className="hidden group-data-[state=open]:inline">Hide</span>
                <span className="group-data-[state=open]:hidden">Show recommendation</span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <p className="border-t border-border/60 px-3 pb-3 pt-2 text-sm text-muted-foreground">
                  {opportunity.recommendation}
                </p>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      }
      footer={
        renderAction ? (
          <ListCardTrailingActionsFooter>{renderAction(opportunity)}</ListCardTrailingActionsFooter>
        ) : undefined
      }
      footerClassName={renderAction ? 'pointer-events-auto px-0' : undefined}
      bodyClassName={
        opportunity.evaluation_id || opportunity.recommendation ? 'pointer-events-auto' : undefined
      }
    />
  )
}
